<script setup>

import {computed, inject, onMounted, ref} from 'vue'
import API from "@/classes/global/API";

import StickyGrid from "@/components/common/other/StickyGrid";
import Button from "@/components/common/input/Button";
import StaticWidget from "@/components/common/other/StaticWidget";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown";
import StaticTooltip from "@/components/common/other/StaticTooltip";
import DateInput from "@/components/common/input/DateInput";
import DropDownTextArea from "@/components/common/input/DropDownTextArea";
import SearchStockPillGroup from "@/components/warehouse/SearchStockPillGroup";
import GridCellDetail from "@/components/common/other/GridCellDetail";
import MoreInfoPopOutPanel from "@/components/common/other/MoreInfoPopOutPanel";
import StockDescriptions from "@/components/warehouse/StockDescriptions";
import Modal from "@/components/common/other/Modal.vue";
import AddressEntry from "@/components/common/other/AddressEntry.vue";
import ContactDetail from "@/classes/bookings/ContactDetail";
import ContactEntry from "@/components/common/other/ContactEntry.vue";
import Address from "@/classes/global/Address";
import PickStockConsignee from "@/classes/stock/PickStockConsignee";
import PickStock from "@/classes/viewmodels/PickStock"

import {useStore} from 'vuex';
import {useRoute} from "vue-router";
import {useMapAddressAndContacts} from "@/composables/common/useMapAddressAndContacts";
import {useClearDescriptionHeaders} from "@/composables/stock/useClearDescriptionHeaders";
import {useCheckSearchCriteriaExists} from "@/composables/common/useCheckSearchCriteriaExists";
import {useTimeGenerator} from "@/composables/common/useTimeGenerator";
import {PickStockValidation} from "@/classes/stock/PickStockValidation";
import {useValidateRequiredFields} from "@/composables/common/useValidateRequiredFields";
import {useGetValidatedFields} from "@/composables/common/useGetValidatedFields";

import CheckBox from "@/components/common/input/CheckBox.vue";
import NumberOnlyInput from "@/components/common/input/NumberOnlyInput.vue";
import PickStockSave from "@/classes/stock/PickStockSave";
import Notification from "@/classes/component-classes/Notification";

import router from "@/router";

const store = useStore();
const route = useRoute();
const pickStock = inject("pickStock");

const routeGuardErrorPage = 'routeGuardErrorPage';

const isEdit = ref(false);

const validationViewModel = inject("validationViewModel");

const props = defineProps({

  modelValue: {}
})

const emits = defineEmits(['update-picked-items']);

const address = computed(() => {

  if (props.modelValue.openEditConsigneeModal) {

    props.modelValue.newConsigneeCompany = JSON.parse(JSON.stringify(props.modelValue.consignee));

  }

  return props.modelValue.newConsigneeCompany;
})

const contact = computed(() => {

  if (props.modelValue.openAddContactModal) {

    delete(props.modelValue.newConsigneeContact.value);

    return props.modelValue.newConsigneeContact;
  }

  return props.modelValue.consignee.selectedContact;
})

onMounted(() => {

  prepareMetaData();

})

async function prepareMetaData() {

  const response = await API.getStockMetaData('pickStockValidatedFields');

  if (response) props.modelValue.metaData = response;

  props.modelValue.allowOverPick = await API.getStockOverPickDetail(store.state.owner);

  if (route.params.id !== 'new') {

    isEdit.value = true;

    let warehouseDetails = await API.getWarehousePickStockDetail(route.params.id);

    if (warehouseDetails) {

      props.modelValue.warehouseJobNumber = warehouseDetails.warehouse.whjobNumber;
      props.modelValue.customerRef = warehouseDetails.warehouse.custRef
      props.modelValue.pickedItems = warehouseDetails.pickedItems;
      props.modelValue.releaseDate = warehouseDetails.warehouse.releaseDate ?? '';
      props.modelValue.releaseTime = warehouseDetails.warehouse.releaseTime ?? '';
      props.modelValue.deliveryDate = warehouseDetails.warehouse.deliveryDate ?? '';
      props.modelValue.action = warehouseDetails.warehouse.action;

      props.modelValue.consignee.selectedContact.value = warehouseDetails.warehouse.deliveryContact ?? '';

      let splitContactName = props.modelValue.consignee.selectedContact.value.split(" ");

      props.modelValue.consignee.selectedContact.title = splitContactName[0];
      props.modelValue.consignee.selectedContact.firstName = splitContactName[1];
      props.modelValue.consignee.selectedContact.lastName = splitContactName[2];
      props.modelValue.consignee.selectedContact.phone = warehouseDetails.warehouse.deliveryPhone;

      mapConsigneeAddress(warehouseDetails);
      // Due to consignee & contact id's not being saved, this code gets by addresslines / phone and selects from metatdata lists

      let addressValues = {

        companyName: warehouseDetails.warehouse.deliveryCompanyName,
        addressLine1: warehouseDetails.warehouse.deliveryAddress1,
        addressLine2: warehouseDetails.warehouse.deliveryAddress2,
        addressLine3: warehouseDetails.warehouse.deliveryAddress3,
        addressLine4: warehouseDetails.warehouse.deliveryAddress4,
        postalCode: warehouseDetails.warehouse.deliveryPostCode,
        country: warehouseDetails.warehouse.deliveryCountry

      }

      try {

        let addressId = await API.getCompanyDetailByAddress(addressValues);

        if (addressId) {

          let checkCompanyExists = props.modelValue.metaData.consignees.find(x => x.id === addressId.toString());

          if (checkCompanyExists) {

            props.modelValue.consignee.id = checkCompanyExists.id;

            let response = await API.getCompanyDetail(checkCompanyExists.id, route);
            if (response) useMapAddressAndContacts(response, props.modelValue, "consignee");

          }
        }

        let contactWithId = props.modelValue.consignee.contacts.find(x => x.phone === warehouseDetails.warehouse.deliveryPhone);
        if (contactWithId) mapConsigneeContact(contactWithId)

      } catch (e) {

        console.log(e)
      }
    } else {

      router.push({name: routeGuardErrorPage});
    }
  }
}

async function getCompanyDetails(name) {

  props.modelValue.consignee.name = name.originValue;

  let response = await API.getCompanyDetail(name.id, route)

  if (response) useMapAddressAndContacts(response, props.modelValue, "consignee");

}

function mapConsigneeAddress(address) {

  // Address is not a saved record, display on screen
  props.modelValue.consignee.name = address.warehouse.deliveryCompanyName;
  props.modelValue.consignee.addressLine1 = address.warehouse.deliveryAddress1;
  props.modelValue.consignee.addressLine2 = address.warehouse.deliveryAddress2;
  props.modelValue.consignee.city = address.warehouse.deliveryAddress3;
  props.modelValue.consignee.county = address.warehouse.deliveryAddress4;
  props.modelValue.consignee.country = address.warehouse.deliveryCountry;
  props.modelValue.consignee.postCode = address.warehouse.deliveryPostCode;
}

function mapConsigneeContact(contact) {

  // Contact is a saved record, add extra data found in ddl meta data
  props.modelValue.consignee.selectedContact.id = contact.id;
  props.modelValue.consignee.selectedContact.email = contact.email;

}

function toggleSearchGroup(button) {

  if (button && props.modelValue.searchGroup) return;
  if (!button && !props.modelValue.searchGroup) return;

  props.modelValue.searchGroup = !props.modelValue.searchGroup;
}

async function getStockHeaders(selected) {

  useClearDescriptionHeaders(props.modelValue);

  const response = await API.getStockDescriptions(route, selected.id);

  if (response) {

    for (const each in response) {

      if (response[each]) {

        props.modelValue.skuGroupSelected = true;
        props.modelValue.searchPills[each].enabled = true;
        props.modelValue.searchPills[each].label = response[each];

      } else {

        props.modelValue.searchPills[each].enabled = false;

      }
    }
  }

  props.modelValue.searchCriteria.skuGroup = selected.id;
  handlePickStockSearch();

}

async function clearHeaders() {

  useClearDescriptionHeaders(props.modelValue);

  if (useCheckSearchCriteriaExists(props.modelValue.searchCriteria)) {

    handlePickStockSearch();

  } else {

    props.modelValue.searchData = [];
  }

}

async function handlePickStockSearch() {

  const response = await API.searchStock(props.modelValue.searchCriteria, 'pickstock');

  props.modelValue.searchData = response;

}

function mapSelectedConsigneeContactDetail(contact) {

  props.modelValue.consignee.selectedContact = contact;
}

function handleClearCompany() {

  props.modelValue.consignee = new PickStockConsignee();
}

function disableGridShowButton(skuProperties) {

  for (const each in skuProperties) {
    if (skuProperties[each].value) return true
  }

  return false
}

function handleClearData() {

  props.modelValue.searchData = [];
}

function addNewConsigneeCompany() {

  props.modelValue.newConsigneeCompany = new Address();
  props.modelValue.openNewConsigneeModal = true;
}

function editConsigneeCompany() {

  props.modelValue.confirmUpdateConsignee = false;
  props.modelValue.consignee.name = pickStock.value.consignee.name.split('-')[0].trim();
  props.modelValue.consigneeCopy = pickStock.value.consignee;

  props.modelValue.openEditConsigneeModal = true;
}

function prepareConsigneeSave() {

  if (useValidateRequiredFields(pickStock.value.newConsigneeCompany, validationViewModel.value.pickStock, props.modelValue.metaData.validatedFields)) {

    props.modelValue.confirmSaveConsignee = true;
  }

}

function prepareConsigneeUpdate() {

  if (useValidateRequiredFields(props.modelValue.newConsigneeCompany, validationViewModel.value.pickStock, props.modelValue.metaData.validatedFields)) {

    props.modelValue.confirmUpdateConsignee = true;
  }

}

async function saveNewConsigneeCompany() {

  props.modelValue.newConsigneeCompany.newCompanySaved = true;

  const addressDetail = await saveCompanyDetail(props.modelValue.newConsigneeCompany);

  if (addressDetail) {

    props.modelValue.newConsigneeCompany.id = addressDetail.id;
    props.modelValue.confirmSaveConsignee = false;

    useMapAddressAndContacts(addressDetail, props.modelValue, 'consignee')
  }

}

async function saveCompanyDetail(company, isEdit) {

  company.country = company.country.split('-')[0].trim();

  // This just satisfies re-use of the API
  company.isShipperAddress = false;

  const addressDetail = await API.saveAddress('consignee', company);

  if (addressDetail) {

    store.state.banners.success = new Notification(true, 'The Data Was Successfully Saved');

    if (isEdit) {

      props.modelValue.openEditConsigneeModal = false;
      mapEditedConsignee(company);
    }
  }

  return addressDetail;
}

function mapEditedConsignee(company) {

  props.modelValue.consignee.name = company.name;
  props.modelValue.consignee.addressLine1 = company.addressLine1;
  props.modelValue.consignee.addressLine2 = company.addressLine2;
  props.modelValue.consignee.city = company.city;
  props.modelValue.consignee.county = company.county;
  props.modelValue.consignee.postCode = company.postCode;
  props.modelValue.consignee.country = company.country;
  props.modelValue.vatNumber = company.vatNumber;
}


function cancelConsigneeSave(isEdit) {

  if (isEdit) return props.modelValue.confirmUpdateConsignee = false;

  props.modelValue.confirmSaveConsignee = false;
}

async function saveEditedConsigneeCompany(company) {

  if (!props.modelValue.keepExistingContacts) company.contacts = [];

  props.modelValue.confirmUpdateConsignee = false;
  saveCompanyDetail(company, true);
}

async function saveConsigneeContact(contact) {

  if (useValidateRequiredFields(contact, validationViewModel.value.pickStock, props.modelValue.metaData.validatedFields)) {

    let emailValid = store.state.emailRegExCheck.test(contact.email);

    if(!emailValid) {

      validationViewModel.value.pickStock.email.messageNumber = 1;
      validationViewModel.value.pickStock.email.value = true;

      return
    }

    // contact.value is set to send to server model, then server model treats it as FullName for db entry
    contact.value = contact.title + ' ' + contact.firstName + ' ' + contact.lastName;

    if (contact.id === 0) {

      props.modelValue.consignee.contacts.push(contact);

      mapSelectedConsigneeContactDetail(contact);

    } else {

      let existingContact = props.modelValue.consignee.contacts.find(x => x.id === contact.id);

      mapEditedContact(contact, existingContact);
    }

    const addressDetail = await saveCompanyDetail(props.modelValue.consignee);

    if (addressDetail) {

      contact.id = addressDetail.contacts[addressDetail.contacts.length - 1].id;
      props.modelValue.newConsigneeContact.newContactSaved = true;
    }

  }

}

function mapEditedContact(newContactData, existingContactData) {

  existingContactData.id = newContactData.id;
  existingContactData.title = newContactData.title;
  existingContactData.firstName = newContactData.firstName;
  existingContactData.lastName = newContactData.lastName;
  existingContactData.department = newContactData.lastName;
  existingContactData.phone = newContactData.phone;
  existingContactData.email = newContactData.email;
  existingContactData.value = newContactData.value;
}

function editNewCompany() {

  props.modelValue.newConsigneeCompany.newCompanySaved = false;
}

function clearConsigneeContact() {

  props.modelValue.consignee.selectedContact = new ContactDetail();
}

function addNewConsigneeContact() {

  props.modelValue.newConsigneeContact = new ContactDetail();
  props.modelValue.openAddContactModal = true;
}

function editConsigneeContact() {

  props.modelValue.openEditContactModal = true;
}

function editNewContact() {

  props.modelValue.newConsigneeContact.newContactSaved = false;
}

function clearNewContactForm() {

  props.modelValue.newConsigneeContact = new ContactDetail();
}

function clearNewConsigneeForm() {

  props.modelValue.newConsigneeCompany = new Address();
}

function handleCloseModal() {

  validationViewModel.value.pickStock = new PickStockValidation();

  clearNewConsigneeForm();
}

function useUnSavedAddress(isEdit) {

  if (useValidateRequiredFields(props.modelValue.newConsigneeCompany, validationViewModel.value.pickStock, props.modelValue.metaData.validatedFields)) {

    const addressFields = ['name', 'addressLine1', 'addressLine2', 'city', 'postCode', 'county', 'country', 'vatNumber'];

    if(props.modelValue.newConsigneeCompany.id !== 0) {

      addressFields.forEach(field => {

        if (props.modelValue.newConsigneeCompany[field] !== props.modelValue.consigneeCopy[field]) {

          return props.modelValue.newConsigneeCompany.id = 0;
        }

      })
    }

    props.modelValue.consignee = props.modelValue.newConsigneeCompany;

    if (isEdit) return props.modelValue.openEditConsigneeModal = false;

    props.modelValue.openNewConsigneeModal = false;
  }
}

function useUnSavedContact(contact) {

  if (useValidateRequiredFields(contact, validationViewModel.value.pickStock, props.modelValue.metaData.validatedFields)) {

    props.modelValue.consignee.selectedContact = props.modelValue.newConsigneeContact;

    props.modelValue.consignee.selectedContact.value = props.modelValue.newConsigneeContact.title + ' '
        + props.modelValue.newConsigneeContact.firstName + ' ' + props.modelValue.newConsigneeContact.lastName;

    props.modelValue.openAddContactModal = false;
    props.modelValue.openEditContactModal = false;

  }

}

async function handlePick(rowData) {

  if (rowData.pickQuantity) {

    rowData.pickQuantity = parseInt(rowData.pickQuantity);

    // Enrich the object to suit the grid
    rowData.comment = '';
    rowData.stockSaved = false;
    rowData.message = 'Waiting To Be Saved';
    rowData.reOrderLevel = [{stockLevel: 0, reOrderLevel: null}];
    rowData.WHJobNum = props.modelValue.warehouseJobNumber;

    // This is to check if we have the same item picked already in the pick grid, there is no id to pull on so we
    // check sku composite for uniqueness and warehouse in case same sku lives in multiple warehouses.
    // Update the same sku allocated number instead of creating a new line.
    let existingPickedItem = props.modelValue.pickedItems.find((item) => {
      return rowData.skuComposite === item.skuComposite && rowData.warehouse === item.warehouse;
    })

    if (existingPickedItem) {

      if (checkStockAmount(rowData, existingPickedItem.allocated)) {

        existingPickedItem.allocated = parseInt(existingPickedItem.allocated) + rowData.pickQuantity;
        // rowData.available = parseInt(rowData.total) - parseInt(existingPickedItem.allocated);

        rowData.available = parseInt(rowData.available) - rowData.pickQuantity;
      }

      if (rowData.available === 0) rowData.zero = '#fcdcdc';

      props.modelValue.updateSearchGrid = Math.random().toString(36).substring(2, 15);
      emits('update-picked-items', Math.random().toString(36).substring(2, 15));

    } else {

      // if overpick is not allowed
      if (!props.modelValue.allowOverPick && rowData.pickQuantity > rowData.available) {

        store.state.banners.warning = new Notification(true, 'Over picking stock is not allowed');

        return;

      } else {

        createNewPickLine(rowData);

      }
    }
  }

  rowData.pickQuantity = '';
}

function createNewPickLine(rowData) {

  props.modelValue.pickedItems.push(rowData);
  props.modelValue.openPicksDrawer = true;
  rowData.allocated = rowData.pickQuantity;

  // TODO This could be removed and use search to refresh values
  rowData.available = parseInt(rowData.available) - parseInt(rowData.pickQuantity);

  if (rowData.available === 0) rowData.zero = '#fcdcdc';

  props.modelValue.updateSearchGrid = Math.random().toString(36).substring(2, 15);
  emits('update-picked-items', Math.random().toString(36).substring(2, 15));

}

function checkStockAmount(rowData, allocated) {

  if (!props.modelValue.allowOverPick) {

    if (parseInt(rowData.pickQuantity) > rowData.available) {

      store.state.banners.warning = new Notification(true, 'Over picking stock is not allowed');
      return false;
    }

  }

  return true;
}

async function preparePickSave() {

  const { customerRef, releaseDate, releaseTime, deliveryDate, action, deliveryInstructions } = props.modelValue;

  const validateItems = { customerRef, releaseDate, releaseTime, deliveryDate, action, deliveryInstructions }

  validateItems.value = props.modelValue.consignee.selectedContact.value;

  let valid = useValidateRequiredFields(validateItems, validationViewModel.value.pickStock,
      props.modelValue.metaData.validatedFields);

  if(valid) {

    let saveDetail = new PickStockSave(props.modelValue);

    saveDetail.handlePickSave();

    // handlePickStockSearch();

    emits('update-picked-items', Math.random().toString(36).substring(2, 15));
  }

}

function openPicksDrawer() {

  props.modelValue.openPicksDrawer = !props.modelValue.openPicksDrawer;
}

function handlePickAll(rowData) {

  let checkQty = Math.sign(rowData.available);

  if(!props.modelValue.allowOverPick && checkQty === -1 ) {

    store.state.banners.warning = new Notification(true, 'Over picking stock is not allowed');
    return;
  }

  rowData.pickQuantity = rowData.available;

  handlePick(rowData)
}

function handleNewPick() {

  router.push({ name: 'pickstock', params: { id: 'new' }});

  pickStock.value = new PickStock();
}

</script>

<template class="cmp-pick-stock">
  <StaticWidget title="Delivery Information" height="250" background="#fcfcfc">
    <template #extra-content>
      <Button
          @click="modelValue.openCancelJobModel = true"
          style="margin-left: 20px;"
          type="toolbar"
          text="Cancel Job">
      </Button>
      <div  v-if="props.modelValue.warehouseJobNumber"
            style="margin-left: 80px; font-size: 15px; font-weight: normal">Warehouse Job Number:
        <span style="color: var(--aqua-blue-definition);font-weight: bold;font-size: 16px">
          {{ props.modelValue.warehouseJobNumber }}
        </span>
        <span v-if="isEdit" style="margin-left: 20px; color: rebeccapurple">&#9998; Currenty Editing</span>
      </div>
    </template>
    <template #right-side>

      <Button
          v-if="!props.modelValue.warehouseJobNumber"
          :disabled="!props.modelValue.consignee.name"
          type="primary"
          @click="preparePickSave()"
          text="Save & Submit">
      </Button>
      <Button
          v-else
          type="success"
          :disabled="!props.modelValue.consignee.name"
          @click="preparePickSave()"
          text="Update">
      </Button>
      <Button
          type="warning"
          @click="props.modelValue.openDocsDrawer = true;"
          text="Documents">
      </Button>
      <Button
          v-if="props.modelValue.warehouseJobNumber"
          type="toolbar"
          @click="handleNewPick()"
          text="New Pick Stock">
      </Button>
    </template>
    <div class="cmp-pick-stock_delivery-section-wrapper">
      <div class="cmp-pick-stock_delivery-section-layout">
        <div class="cmp-pick-stock_address-entry-section">
          <div class="cmp-pick-stock_field-button-layout">
            <MultiSearchDropDown
                :key="props.modelValue.metaData.consignees"
                :required-field="useGetValidatedFields(modelValue.metaData.validatedFields, 'name')"
                :allowCustomValue="false"
                :useServerSearch="true"
                :maxLength="50"
                label="Consignee & Address"
                :label-top="true"
                :width="400"
                searchType="consignee"
                v-model="props.modelValue.consignee.name"
                @select="getCompanyDetails($event, 'consignee')"
                @clear="handleClearCompany()"
                :dataSource="props.modelValue.metaData.consignees"
                :selectPrimitiveValue="true">
            </MultiSearchDropDown>
            <div class="cmp-pick-stock_address-buttons">
              <img class="global-icon-button"
                   @click="addNewConsigneeCompany"
                   height="22"
                   width="22"
                   src="@/assets/icons/akar/akar-icons--circle-plus.png">
              <img class="global-icon-button"
                   @click="editConsigneeCompany"
                   v-if="props.modelValue.consignee.name"
                   height="22" width="22"
                   src="@/assets/icons/akar/akar-icons--edit.png">
            </div>
          </div>
          <div class="cmp-pick-stock_address-container">
            <div v-if="props.modelValue.consignee.name" class="cmp-pick-stock_address-details">
              <div title="Address Line 1">{{ props.modelValue.consignee.addressLine1 }}</div>
              <div title="Address Line 2">{{ props.modelValue.consignee.addressLine2 }}</div>
              <div title="City">{{ props.modelValue.consignee.city }}</div>
              <div title="County">{{ props.modelValue.consignee.county }}</div>
              <div title="Post Code">{{ props.modelValue.consignee.postCode }}</div>
              <div title="Country">{{ props.modelValue.consignee.country }}</div>
            </div>
            <div v-if="!props.modelValue.consignee.name" class="cmp-multi-booking_address-placeholder-wrapper">
              <div style="display:flex; align-items: center">
                <img height="26" width="26" src="@/assets/icons/address-marker.png">
                <span>Address Information</span>
              </div>
              <div class="cmp-multi-booking_address-placeholder cmp-multi-booking_a-p1"></div>
              <div class="cmp-multi-booking_address-placeholder cmp-multi-booking_a-p2"></div>
              <div class="cmp-multi-booking_address-placeholder cmp-multi-booking_a-p3"></div>
            </div>
          </div>
          <div style="position: absolute; top: 140px">
            <div class="cmp-pick-stock_field-button-layout">
              <MultiSearchDropDown
                  :key="props.modelValue.consignee.selectedContact.value || props.modelValue.consignee.contacts"
                  :width="400"
                  :maxLength="50"
                  :required-field="useGetValidatedFields(modelValue.metaData.validatedFields, 'value')"
                  v-model:validation-fail="validationViewModel.pickStock.value"
                  label="Contact"
                  style="margin-top: 20px;"
                  :label-top="true"
                  placeholder="Select..."
                  :allowCustomValue="true"
                  @select="mapSelectedConsigneeContactDetail"
                  @clear="clearConsigneeContact"
                  :disableFilter="true"
                  v-model="props.modelValue.consignee.selectedContact.value"
                  :data-source="props.modelValue.consignee.contacts"
                  :selectPrimitiveValue="true">
              </MultiSearchDropDown>
              <div class="cmp-pick-stock_address-buttons" style="margin-top: 20px;">
                <img class="global-icon-button"
                     @click="addNewConsigneeContact()"
                     height="22"
                     width="22"
                     src="@/assets/icons/akar/akar-icons--circle-plus.png">
                <img class="global-icon-button"
                     @click="editConsigneeContact()"
                     v-if="props.modelValue.consignee.selectedContact.value"
                     height="22" width="22"
                     src="@/assets/icons/akar/akar-icons--edit.png">
              </div>
            </div>
            <div class="cmp-pick-stock_contact-container">
              <div class="cmp-pick-stock_contact-details">
                <div class="cmp-pick-stock_contact-info">
                  <div v-if="props.modelValue.consignee.selectedContact.phone">
                    <i class="fa-duotone fa-square-phone cmp-pick-stock_contact-icons"></i>
                    Tel: {{ props.modelValue.consignee.selectedContact.phone }}
                  </div>
                  <div v-if="props.modelValue.consignee.selectedContact.email">
                    <i class="fa-duotone fa-envelope cmp-pick-stock_contact-icons"></i>
                    Email: {{ props.modelValue.consignee.selectedContact.email }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="cmp-pick-stock_delivery-information-area">
          <TextInput
              v-model="props.modelValue.customerRef"
              :required-field="useGetValidatedFields(modelValue.metaData.validatedFields, 'customerRef')"
              v-model:validation-fail="validationViewModel.pickStock.customerRef"
              :width="400"
              :maxLength="50"
              :labelTop="true"
              label="Customer Ref">
          </TextInput>
          <div class="cmp-pick-stock_release-date-area">
            <DateInput
                v-model="props.modelValue.releaseDate"
                :required-field="useGetValidatedFields(modelValue.metaData.validatedFields, 'releaseDate')"
                v-model:validation-fail="validationViewModel.pickStock.releaseDate"
                style="margin-top: 20px"
                :labelWidth="118"
                :labelTop="true"
                label="Release Date">
              <el-date-picker
                  :clearable="false"
                  value-format="DD-MMM-YY"
                  v-model="props.modelValue.releaseDate"></el-date-picker>
            </DateInput>
            <MultiSearchDropDown
                v-model="props.modelValue.releaseTime"
                :required-field="useGetValidatedFields(modelValue.metaData.validatedFields, 'releaseTime')"
                v-model:validation-fail="validationViewModel.pickStock.releaseTime"
                style="margin-top: 20px"
                :width="121"
                :labelWidth="75"
                :labelTop="true"
                :maxLength="50"
                placeholder="Select..."
                :allowCustomValue="false"
                :disableFilter="true"
                :select-primitive-value="true"
                label="Release Time"
                :data-source="useTimeGenerator(24)">
              :selectPrimitiveValue="true">
            </MultiSearchDropDown>
          </div>
          <DateInput
              v-model="props.modelValue.deliveryDate"
              :required-field="useGetValidatedFields(modelValue.metaData.validatedFields, 'deliveryDate')"
              v-model:validation-fail="validationViewModel.pickStock.deliveryDate"
              style="margin-top: 20px"
              :labelWidth="118"
              :labelTop="true"
              label="Delivery Date">
            <el-date-picker
                :clearable="false"
                value-format="DD-MMM-YY"
                v-model="props.modelValue.deliveryDate"></el-date-picker>
          </DateInput>
          <TextInput
              v-model="props.modelValue.action"
              :required-field="useGetValidatedFields(modelValue.metaData.validatedFields, 'action')"
              v-model:validation-fail="validationViewModel.pickStock.action"
              :width="400"
              style="margin-top: 20px"
              :labelTop="true"
              :maxLength="50"
              label="Action">
          </TextInput>
          <DropDownTextArea
              v-model="props.modelValue.deliveryInstructions"
              :required-field="useGetValidatedFields(modelValue.metaData.validatedFields, 'deliveryInstructions')"
              v-model:validation-fail="validationViewModel.pickStock.deliveryInstructions"
              style="margin-top: 20px"
              :labelTop="true"
              :width="400"
              label="Delivery Instructions">
          </DropDownTextArea>
        </div>
      </div>
    </div>
  </StaticWidget>
  <StaticWidget title="Search Stock" height="410">
    <template #extra-content>
      <div style=" margin-left: 80px; font-weight: normal">SKU Group</div>
      <div style="font-weight: normal" class="global-flex-layout">
        <MultiSearchDropDown
            :key="props.modelValue.metaData.skuGroups"
            :width="320"
            :labelWidth="85"
            :maxLength="50"
            placeholder="Select..."
            @select="getStockHeaders"
            @clear="clearHeaders"
            :allowCustomValue="false"
            :disableFilter="true"
            v-model="props.modelValue.searchCriteria.skuGroup"
            :dataSource="props.modelValue.metaData.skuGroups"
            :selectPrimitiveValue="true">
        </MultiSearchDropDown>
        <StaticTooltip :icon="false">
          <template #content>
            <div style="width: 340px;">
              <strong>Search</strong> by completing any field and press any search icon
              <span><i class="fa-duotone fa-square-arrow-right" style="color: #114362"></i></span> or press enter within
              the field.
              Any fields with data are considered when searching records, some criteria may return no results. Selecting
              a
              SKU Group will bring new search criteria where available. A search group toggle will appear to view other
              fields.
              Press Refresh to return the latest records for the current search, or reset the data to initial records.
            </div>
          </template>
          <i :class="store.state.globalHelpIcon"></i>
        </StaticTooltip>
        <div v-if="props.modelValue.skuGroupSelected" class="cmp-stock_search-group-selection">
          <span>Search Group</span>
          <Button
              @click="toggleSearchGroup(true)"
              type="toolbar"
              text="1"
              :class="{'cmp-stock_search-group-selection-icon-active' : props.modelValue.searchGroup}"
              style="width: 30px;">
          </Button>
          <Button
              @click="toggleSearchGroup(false)"
              type="toolbar"
              text="2"
              :class="{'cmp-stock_search-group-selection-icon-active' : !props.modelValue.searchGroup}"
              style="width: 30px;">
          </Button>
        </div>
      </div>
    </template>
    <template #right-side>
      <Button
          @click="openPicksDrawer"
          icon="<i class='fa-duotone fa-grid-2'></i>"
          type="secondary"
          :badgeData="props.modelValue.pickedItems.length"
          text="View Picks">
      </Button>
    </template>
    <SearchStockPillGroup
        url="pickstock"
        @clear="handleClearData"
        v-model="props.modelValue">
    </SearchStockPillGroup>
    <div style="margin-top: 10px;">
      <StickyGrid
          :key="props.modelValue.updateSearchGrid"
          :allowGridMenu="false"
          :height="280"
          :width="98"
          :page-size="200"
          :selectable="false"
          :highlightRows="false"
          :allow-column-resize="false"
          :row-selection="false"
          v-model:currentPage="props.modelValue.searchResultCurrentPage"
          v-model:currentPaginationRange="props.modelValue.searchResultPaginationRange"
          :dataSource="props.modelValue.searchData"
          :columns="props.modelValue.searchGridColumns">
        <!--        <template v-slot:available="row">-->
        <!--          <div style="display: grid; grid-template-columns: 40px 100px;">-->
        <!--            {{row.rowData.available}}<span v-if="row.rowData.isLowStock" style="font-size: 11px; color: #b04545">-->
        <!--            <i class="fa-solid fa-triangle-exclamation" style="padding-top: 6px"></i> Low Stock!</span>-->
        <!--          </div>-->
        <!--        </template>-->
        <template #right-content>
          <div class="cmp-pick-stock_grid-totals-message">

          </div>
        </template>
        <template v-slot:pick="row">
          <Button
              :disabled="row.rowData.isZeroQuantity"
              @click="handlePick(row.rowData)"
              icon="<i class='fa-duotone fa-circle-plus'></i>"
              type="plain"
              text="Pick">
          </Button>
        </template>
        <template v-slot:pickAll="row">
          <Button
              :disabled="row.rowData.isZeroQuantity"
              @click="handlePickAll(row.rowData)"
              icon="<i class='fa-duotone fa-circle-plus'></i>"
              type="toolbar"
              text="Pick All">
          </Button>
        </template>
        <template #menu-slot-items v-if="props.modelValue.searchData.length > 0">
          <Button
              @click="props.modelValue.openPicksDrawer = !props.modelValue.openPicksDrawer"
              icon="<i class='fa-duotone fa-grid-2'></i>"
              type="toolbar"
              :badgeData="props.modelValue.pickedItems.length"
              text="View Picks">
          </Button>
        </template>
        <template v-slot:pickQuantity="row">
          <NumberOnlyInput
              @keydown.enter="handlePick(row.rowData)"
              :disable="row.rowData.isZeroQuantity && !props.modelValue.allowOverPick"
              v-model="row.rowData.pickQuantity"
              :allowDecimal="false"
              :width="60">
          </NumberOnlyInput>
        </template>
        <!--        <template v-slot:comments="row">-->
        <!--          <DropDownTextArea-->
        <!--              v-model="row.rowData.comments"-->
        <!--              :width="310">-->
        <!--          </DropDownTextArea>-->
        <!--        </template>-->
        <template v-slot:skuProperties="row">
          <GridCellDetail :clickToView="true" v-if="disableGridShowButton(row.grid.skuProperties)">
            <template #details>
              <MoreInfoPopOutPanel
                  v-model="row.grid">
                <template #header>Stock Item Properties</template>
                <template #header-extra>SKU Number:
                  <span style="color: var(--aqua-blue-definition)"> {{ row.grid.skuNumber }}</span></template>
                <template #content>
                  <StockDescriptions v-model="row.grid.skuProperties"></StockDescriptions>
                </template>
              </MoreInfoPopOutPanel>
            </template>
            <template #cell-data>
              <Button
                  v-if="disableGridShowButton(row.grid.skuProperties)"
                  type="toolbar"
                  text="View">
              </Button>
            </template>
          </GridCellDetail>
        </template>
      </StickyGrid>
    </div>
  </StaticWidget>
  <Modal
      v-model="props.modelValue.openNewConsigneeModal"
      :showClose="false"
      @close="handleCloseModal()"
      title="New Consignee"
      width="480px"
      height="380px">
    <template #icon><i class="fa-solid fa-circle-plus global-add-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <AddressEntry
            v-if="!props.modelValue.confirmSaveConsignee"
            validation-model="pickStock"
            :countries="props.modelValue.metaData.countries"
            v-model:requiredFields="props.modelValue.metaData.validatedFields"
            v-model="address">
          <template #vat-number-flex-content
                    v-if="props.modelValue.newConsigneeCompany.name && props.modelValue.newConsigneeCompany.addressLine1">
            <div class="cmp-pick-stock_address-link"><span @click="useUnSavedAddress(false)">Use this address</span>
              <i title="Use the address only for this job. It will not be saved. If you save later a NEW consignee will
                  be added to your database." class="fa fa-info-circle icon-info"/>
            </div>
          </template>
        </AddressEntry>
        <div v-else class="cmp-pick-stock_address-list">
          <div style="font-weight: bold">{{ props.modelValue.newConsigneeCompany.name }}</div>
          <div style="font-weight: normal">
            <div>{{ props.modelValue.newConsigneeCompany.addressLine1 }}</div>
            <div>{{ props.modelValue.newConsigneeCompany.addressLine2 }}</div>
            <div>{{ props.modelValue.newConsigneeCompany.city }}</div>
            <div>{{ props.modelValue.newConsigneeCompany.county }}</div>
            <div>{{ props.modelValue.newConsigneeCompany.postCode }}</div>
            <div>{{ props.modelValue.newConsigneeCompany.country }}</div>
          </div>
          <div class="cmp-pick-stock_confirm-save-address">
            <span v-if="modelValue.newConsigneeCompany.newCompanySaved">Update Consignee?</span>
            <span v-else>Create New Consignee?</span>
            <Button
                style="margin-left: 20px"
                @click="saveNewConsigneeCompany"
                text="Yes"
                type="success">
            </Button>
            <Button
                @click="cancelConsigneeSave(false)"
                text="No"
                type="secondary">
            </Button>
          </div>
        </div>
      </div>
    </template>
    <template #footer v-if="!props.modelValue.confirmSaveConsignee">
      <Button
          v-if="props.modelValue.newConsigneeCompany.newCompanySaved"
          @click="editNewCompany"
          icon="<i class='fa-duotone fa-pen-to-square'></i>"
          text="Edit"
          type="info">
      </Button>
      <Button
          v-if="props.modelValue.newConsigneeCompany.newCompanySaved"
          @click="clearNewConsigneeForm"
          icon="<i class='fa-light fa-circle-plus'></i>"
          text="Add New"
          type="secondary">
      </Button>
      <Button
          v-if="!props.modelValue.newConsigneeCompany.newCompanySaved"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          @click="prepareConsigneeSave"
          text="Save">
      </Button>
    </template>
  </Modal>
  <Modal
      v-model="props.modelValue.openEditConsigneeModal"
      :showClose="false"
      @close="handleCloseModal('addContact')"
      title="Edit Consignee"
      width="480px"
      height="420px">
    <template #icon><i class="fa-solid fa-pen-to-square global-edit-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <AddressEntry
            v-if="!props.modelValue.confirmUpdateConsignee"
            validation-model="pickStock"
            v-model:requiredFields="props.modelValue.metaData.validatedFields"
            :countries="props.modelValue.metaData.countries"
            v-model="address">
          <template #vat-number-flex-content>
            <div class="cmp-pick-stock_address-link"><span @click="useUnSavedAddress(true)">Use this address</span>
              <i title="Use the address only for this job. It will not save any changes for this consignee.
                If you make changes and save after using the address, a NEW consignee will be added to your database."
                 class="fa fa-info-circle icon-info"/>
            </div>
          </template>
          <template #keep-contacts>
            <CheckBox
                v-model="props.modelValue.keepExistingContacts"
                label="keep existing contacts">
            </CheckBox>
          </template>
        </AddressEntry>
        <div v-else class="cmp-pick-stock_address-list">
          <div style="display: flex; gap:160px">
            <div>
              <div style="font-weight: bold">{{ props.modelValue.newConsigneeCompany.name }}</div>
              <div style="font-weight: normal">
                <div>{{ props.modelValue.newConsigneeCompany.addressLine1 }}</div>
                <div>{{ props.modelValue.newConsigneeCompany.addressLine2 }}</div>
                <div>{{ props.modelValue.newConsigneeCompany.city }}</div>
                <div>{{ props.modelValue.newConsigneeCompany.county }}</div>
                <div>{{ props.modelValue.newConsigneeCompany.postCode }}</div>
                <div>{{ props.modelValue.newConsigneeCompany.country }}</div>
              </div>
            </div>
          </div>
          <div class="cmp-pick-stock_confirm-save-address">
            <span v-if="props.modelValue.newConsigneeCompany.id !== 0">Update Consignee?</span>
            <span v-else>Create New Consignee?</span>
            <Button
                style="margin-left: 20px"
                @click="saveEditedConsigneeCompany(address)"
                text="Yes"
                type="success">
            </Button>
            <Button
                @click="cancelConsigneeSave(true)"
                text="No"
                type="secondary">
            </Button>
          </div>
          <span style="font-size: 11px" v-if="props.modelValue.newConsigneeCompany.id === 0">
            This was previously used as a one time address. Saving will create a new contact!
          </span>
        </div>
      </div>
    </template>
    <template #footer>
      <Button
          v-if="!props.modelValue.confirmUpdateConsignee"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          @click="prepareConsigneeUpdate()"
          text="Save">
      </Button>
    </template>
  </Modal>
  <Modal
      v-model="props.modelValue.openAddContactModal"
      :showClose="false"
      @close="handleCloseModal('addContact')"
      title="Add Contact"
      width="350px"
      height="280px">
    <template #icon><i class="fa-solid fa-circle-plus global-add-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <ContactEntry
            @use-contact="useUnSavedContact(contact)"
            validation-model="pickStock"
            v-model:requiredFields="props.modelValue.metaData.validatedFields"
            v-model="contact">
        </ContactEntry>
      </div>
    </template>
    <template #footer v-if="props.modelValue.consignee.id !== 0">
      <Button
          v-if="props.modelValue.newConsigneeContact.newContactSaved"
          @click="editNewContact"
          icon="<i class='fa-duotone fa-pen-to-square'></i>"
          text="Edit"
          type="info">
      </Button>
      <Button
          v-if="props.modelValue.newConsigneeContact.newContactSaved"
          @click="clearNewContactForm"
          icon="<i class='fa-light fa-circle-plus'></i>"
          text="Add New"
          type="secondary">
      </Button>
      <Button
          v-if="!props.modelValue.newConsigneeContact.newContactSaved"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          @click="saveConsigneeContact(contact)"
          text="Save">
      </Button>
    </template>
  </Modal>
  <Modal
      v-model="props.modelValue.openEditContactModal"
      :showClose="false"
      @close="handleCloseModal('addContact')"
      title="Edit Contact"
      width="350px"
      height="280px">
    <template #icon><i class="fa-solid fa-circle-plus global-add-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <ContactEntry
            @use-contact="useUnSavedContact(contact)"
            validation-model="pickStock"
            v-model:requiredFields="props.modelValue.metaData.validatedFields"
            v-model="contact">
        </ContactEntry>
      </div>
    </template>
    <template #footer>
      <Button
          v-if="props.modelValue.consignee.id !== 0"
          @click="saveConsigneeContact(contact)"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          text="Save">
      </Button>
    </template>
  </Modal>

  <Modal
      v-model="modelValue.openCancelJobModel"
      :showClose="false"
      title="Cancel Job"
      width="380px"
      height="100px">
    <template #body>
      <div class="cmp-pick-stock_cancel-job">&#128711; Are you sure you want to cancel this Job?
        <div style="margin-top: 20px; color: #0a0a0a">
          This will restore any picked stock and remove the warehouse job your records!
        </div>
      </div>
    </template>
    <template #footer>
      <Button
          @click="modelValue.openCancelJobModel = false"
          icon="<i class='fa-duotone fa-pen-to-square'></i>"
          text="No"
          type="danger">
      </Button>
      <Button
          icon="<i class='fa-light fa-circle-plus'></i>"
          text="Yes "
          type="primary">
      </Button>
    </template>
  </Modal>

<!--  <Modal-->
<!--      v-model="modelValue.openNoPicksModel"-->
<!--      :showClose="false"-->
<!--      @close="handleCloseModal()"-->
<!--      title="Warning"-->
<!--      width="480px"-->
<!--      height="100px">-->
<!--    <template #body>-->
<!--      <div class="cmp-pick-stock_cancel-job">&#128711; You are removing all picked stock from this job!-->
<!--        <div style="margin-top: 15px; color: #0a0a0a">Do you wish to cancel this job and remove it from your records?</div>-->
<!--        <div style="margin-top: 5px; font-size: 11px; color: #4f4f4f">If you cancel, the last stock will not be removed</div>-->
<!--      </div>-->
<!--    </template>-->
<!--    <template #footer>-->
<!--      <Button-->
<!--          @click="modelValue.openNoPicksModel = false"-->
<!--          icon="<i class='fa-duotone fa-pen-to-square'></i>"-->
<!--          text="Cancel"-->
<!--          type="toolbar">-->
<!--      </Button>-->
<!--      <Button-->
<!--          @click="modelValue.openNoPicksModel = false"-->
<!--          icon="<i class='fa-duotone fa-pen-to-square'></i>"-->
<!--          text="No"-->
<!--          type="danger">-->
<!--      </Button>-->
<!--      <Button-->
<!--          icon="<i class='fa-light fa-circle-plus'></i>"-->
<!--          text="Yes"-->
<!--          type="primary">-->
<!--      </Button>-->
<!--    </template>-->
<!--  </Modal>-->
</template>

<style lang="scss" scoped>

@import '@/css/booking/multi-booking.scss';

.cmp-pick-stock {

  &_address-entry {
    margin-top: 30px;
  }

  &_header-text {
    font-size: 15px;
    //font-weight: bold;
    color: #383838;
  }

  &_delivery-section-layout {
    margin-top: 5px;
    padding: 10px 0 0 20px;
    display: grid;
    gap: 170px;
    grid-template-columns: 460px 40%;
  }

  &_delivery-section-wrapper {
    height: 400px;
  }

  &_address-save-button {
    padding: 5px 0 0 88px;
  }

  &_field-button-layout {
    display: flex;
    gap: 14px;
    align-items: center;
  }

  &_address-entry-section {
    position: relative;
  }

  &_delivery-information-area {
    padding-left: 70px;
  }

  &_address-details {
    font-size: 13px;
    color: #343434;
    padding: 3px;
  }

  &_contact-info {
    margin-top: 2px;
    font-size: 13px;
    padding-left: 3px;
  }

  &_contact-icons {
    font-size: 13px;
    color: #0e9182;
  }

  &_release-date-area {
    display: flex;
    gap: 170px;
    width: 400px;
    height: 50px;
  }

  &_address-container, &_contact-container {
    margin-top: 6px;
    padding: 3px;
    height: 100px;
    width: 392px;
    background: #ffffff;
    position: relative;
    background: #ffffff;
    box-shadow: var(--box-shadow);
    border-radius: var(--rounded-corners);
  }

  &_contact-container {
    height: 40px;
  }

  &_address-link {
    font-size: 12px;
    color: #0fa4c7;
  }

  &_address-link:hover {
    text-decoration: underline;
  }

  &_address-list {
    font-size: 14px;
  }

  &_confirm-save-address {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 12px;
    height: 80px;
    font-weight: bold;
    color: var(--aqua-blue-definition);
  }

  &_grid-totals-message {
    font-size: 11px;
    margin-left: auto;
    padding-right: 100px;
    padding-top: 5px;
    color: var(--aqua-blue-definition);
  }

  &_address-buttons {
    display: flex;
    gap: 10px;
  }

}

</style>
